import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  ALL_DENIED,
  ALL_GRANTED,
  readConsent,
  saveConsent,
  subscribeConsent,
  pushConsentToGtag,
} from "@/lib/cookie-consent";

export function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [advertising, setAdvertising] = useState(true);

  useEffect(() => {
    const stored = readConsent();
    if (stored) {
      pushConsentToGtag(stored);
      setAnalytics(stored.analytics === "granted");
      setAdvertising(stored.advertising === "granted");
    } else {
      setVisible(true);
    }
    // Re-open banner when preferences are cleared from /cookies.
    return subscribeConsent((c) => {
      if (c === null) {
        setAnalytics(true);
        setAdvertising(true);
        setCustomizing(false);
        setVisible(true);
      } else {
        setAnalytics(c.analytics === "granted");
        setAdvertising(c.advertising === "granted");
        setVisible(false);
      }
    });
  }, []);

  const acceptAll = () => { saveConsent(ALL_GRANTED); setVisible(false); };
  const rejectAll = () => { saveConsent(ALL_DENIED); setVisible(false); };
  const saveChoices = () => {
    saveConsent({
      analytics: analytics ? "granted" : "denied",
      advertising: advertising ? "granted" : "denied",
    });
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-50 px-3 pb-3 sm:px-4 sm:pb-4"
    >
      <div className="mx-auto max-w-3xl rounded-lg border border-border/60 bg-background/95 p-4 shadow-lg backdrop-blur">
        {!customizing ? (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-foreground">
              We use cookies for essential site features and, with your consent, analytics &amp; ads to improve your experience.{" "}
              <Link to="/cookies" className="font-medium text-primary underline underline-offset-4" aria-label="Read our full cookie policy">
                Read our cookie policy
              </Link>.
            </p>
            <div className="flex shrink-0 flex-wrap gap-2">
              <Button variant="ghost" size="sm" onClick={() => setCustomizing(true)}>Customize</Button>
              <Button variant="outline" size="sm" onClick={rejectAll}>Reject all</Button>
              <Button size="sm" onClick={acceptAll}>Accept all</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <h2 className="text-sm font-semibold">Cookie preferences</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Choose which categories to allow. You can change this any time on the{" "}
                <Link to="/cookies" className="font-medium text-primary underline underline-offset-4">cookie policy</Link> page.
              </p>
            </div>
            <ConsentRow
              id="ess"
              title="Essential"
              description="Required for authentication, sessions, and storing your consent. Always active."
              checked
              disabled
            />
            <ConsentRow
              id="ana"
              title="Analytics"
              description="Helps us understand how the site is used (Google Analytics)."
              checked={analytics}
              onChange={setAnalytics}
            />
            <ConsentRow
              id="ads"
              title="Advertising"
              description="Personalised ads & conversion measurement (Google Ads, AdSense)."
              checked={advertising}
              onChange={setAdvertising}
            />
            <div className="flex flex-wrap justify-end gap-2 pt-2">
              <Button variant="ghost" size="sm" onClick={() => setCustomizing(false)}>Back</Button>
              <Button variant="outline" size="sm" onClick={rejectAll}>Reject all</Button>
              <Button variant="outline" size="sm" onClick={acceptAll}>Accept all</Button>
              <Button size="sm" onClick={saveChoices}>Save choices</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ConsentRow({
  id, title, description, checked, disabled, onChange,
}: {
  id: string;
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-md border border-border/50 p-3">
      <div className="min-w-0">
        <Label htmlFor={`cc-${id}`} className="text-sm font-medium">{title}</Label>
        <p className="mt-1 text-xs text-muted-foreground">{description}</p>
      </div>
      <Switch
        id={`cc-${id}`}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
        aria-label={`${title} cookies`}
      />
    </div>
  );
}
